/**
 * Modale "Suggérer des centres d'intérêt via IA"
 * ──────────────────────────────────────────────
 *
 * Pourquoi
 * --------
 * Un utilisateur qui vient de définir son positionnement n'est pas toujours capable
 * d'identifier seul les sujets à surveiller dans son secteur. Cette modale propose
 * 3-5 centres d'intérêt cohérents (avec leurs mots-clés associés) issus du
 * positionnement, et laisse l'utilisateur cocher ceux qu'il veut adopter.
 *
 * Flow
 * ----
 *   1. Modale ouverte → spinner pendant que l'API tourne (POST /watch/suggest-topics).
 *   2. Affichage de la liste avec checkbox (tous cochés par défaut).
 *   3. Clic "Ajouter sélection" → boucle d'appels `onAddTopic()` pour chaque case cochée.
 *   4. Fermeture.
 *
 * Sécurité
 * --------
 * Si la modale est ouverte alors que le positionnement n'est pas défini, le backend
 * renverra 400. On affiche un message d'erreur à la place de la liste.
 */
import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../../ui/dialog';
import { Loader2, Sparkles, X, Plus, AlertTriangle } from 'lucide-react';
import { AnimatedIconButton } from '../../common/AnimatedIconButton';
import { TypingDots } from '../../common/TypingDots';
import { useLanguage } from '../../../contexts/LanguageContext';
import { watchTopicsService, type SuggestedTopic } from '../../../services/watchTopicsService';
import { toastService } from '../../../services/toastService';
import { Logger } from '../../../utils/logger';
import { extractApiErrorMessage } from '../../../utils/apiError';
import type { WatchTopic } from '../../../types';

interface SuggestTopicsModalProps {
  open: boolean;
  spaceId: string | null | undefined;
  onClose: () => void;
  /** Callback d'ajout — réutilise le `onAddTopic` du parent (WatchTab). */
  onAddTopic: (topic: Omit<WatchTopic, 'id'>) => void;
}

export function SuggestTopicsModal({ open, spaceId, onClose, onAddTopic }: SuggestTopicsModalProps) {
  const { t } = useLanguage();
  const [isLoading, setIsLoading] = useState(false);
  const [topics, setTopics] = useState<SuggestedTopic[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);

  // Fetch à l'ouverture
  useEffect(() => {
    if (!open || !spaceId) return;
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    setTopics([]);
    setSelected(new Set());
    watchTopicsService
      .suggestTopics(spaceId)
      .then((res) => {
        if (cancelled) return;
        const list = res.topics ?? [];
        setTopics(list);
        // Par défaut, tout est coché — l'utilisateur décoche au besoin.
        setSelected(new Set(list.map((_, i) => i)));
      })
      .catch((err: unknown) => {
        // V4-FRONT-ANY-USAGE batch 6 2026-05-29
        if (cancelled) return;
        Logger.error('[SuggestTopicsModal] suggestTopics failed', err);
        setError(extractApiErrorMessage(err, t.watch.suggestError || 'Impossible de générer des suggestions. Vérifiez votre positionnement.'));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, spaceId]);

  const toggle = (idx: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const handleAdd = async () => {
    if (selected.size === 0) return;
    setIsAdding(true);
    try {
      // On ajoute en séquence pour ne pas surcharger l'API et garder un ordre stable.
      // `onAddTopic` est synchrone côté contrat (cf WatchPanelProps) mais persiste
      // de manière asynchrone via le service parent — on ne peut pas l'attendre,
      // donc on déclenche tout puis on ferme.
      for (const idx of Array.from(selected).sort((a, b) => a - b)) {
        const t = topics[idx];
        if (!t) continue;
        onAddTopic({
          prompt: t.name + (t.description ? `\n${t.description}` : ''),
          keywords: t.keywords,
          enabled: true,
        } as Omit<WatchTopic, 'id'>);
      }
      toastService.success(t.watch.suggestSuccess?.replace('{count}', String(selected.size)) ?? `${selected.size} centre(s) d'intérêt ajouté(s)`);
      onClose();
    } catch (e) {
      Logger.error('[SuggestTopicsModal] handleAdd failed', e);
      toastService.error(t.watch.suggestErrorAdd ?? "Erreur lors de l'ajout des thématiques");
    } finally {
      setIsAdding(false);
    }
  };

  const allSelected = topics.length > 0 && selected.size === topics.length;
  const noneSelected = selected.size === 0;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="max-w-2xl backdrop-blur-sm"
        style={{ backgroundColor: 'rgba(var(--color-background-rgb, 255, 255, 255), 0.95)' }}
      >
        <DialogTitle className="flex items-center gap-2 pr-8" style={{ color: 'var(--color-text-primary)' }}>
          <Sparkles className="w-5 h-5" style={{ color: 'var(--color-primary)' }} />
          {t.watch.suggestTitle ?? 'Suggérer des centres d\'intérêt'}
        </DialogTitle>
        <DialogDescription style={{ color: 'var(--color-text-secondary)' }}>
          {t.watch.suggestDescription ??
            'Basé sur votre positionnement, voici des centres d\'intérêt que nous vous proposons de surveiller. Décochez ceux qui ne vous intéressent pas.'}
        </DialogDescription>

        <div>
          {/* UX backlog 1.2 : Toggle "Tout sélectionner" + compteur SORTI de la zone scrollable
              pour rester visible quand l'utilisateur scrolle dans une longue liste. */}
          {!isLoading && !error && topics.length > 0 && (
            <div className="flex items-center justify-between mt-4 mb-2 pb-2" style={{ borderBottom: '1px solid var(--color-border)' }}>
              <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                {selected.size}/{topics.length} {t.common.selectedLabel ?? 'sélectionnés'}
              </span>
              <button
                type="button"
                onClick={() => setSelected(allSelected ? new Set() : new Set(topics.map((_, i) => i)))}
                className="text-xs underline cursor-pointer"
                style={{ color: 'var(--color-primary)' }}
              >
                {allSelected
                  ? (t.common.deselectAll ?? 'Tout désélectionner')
                  : (t.common.selectAll ?? 'Tout sélectionner')}
              </button>
            </div>
          )}
        <div className="max-h-[50vh] overflow-y-auto relative">
          {/* UX backlog 1.3 : overlay opaque pendant l'ajout pour bloquer la double soumission
              et donner un feedback explicite que l'opération est en cours côté backend. */}
          {isAdding && (
            <div
              className="absolute inset-0 flex items-center justify-center gap-3 z-10"
              style={{ backgroundColor: 'rgba(0, 0, 0, 0.3)', backdropFilter: 'blur(4px)' }}
            >
              <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--color-text-light)' }} />
              <span className="text-sm font-medium" style={{ color: 'var(--color-text-light)' }}>
                {(t.watch.suggestAdding ?? 'Ajout de {count} centre(s) d\'intérêt en cours…').replace('{count}', String(selected.size))}
              </span>
            </div>
          )}
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <TypingDots
                size={8}
                label={t.watch.suggestLoading ?? 'Génération en cours…'}
              />
            </div>
          ) : error ? (
            <div className="flex items-start gap-3 p-4 rounded-lg" style={{ backgroundColor: 'var(--color-danger-light, rgba(239,68,68,0.1))' }}>
              <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-danger)' }} />
              <p className="text-sm" style={{ color: 'var(--color-text-primary)' }}>{error}</p>
            </div>
          ) : topics.length === 0 ? (
            <p className="text-sm py-8 text-center" style={{ color: 'var(--color-text-secondary)' }}>
              {t.watch.suggestEmpty ?? 'Aucune suggestion disponible.'}
            </p>
          ) : (
            <div className="space-y-2">
              {topics.map((topic, idx) => {
                const isChecked = selected.has(idx);
                return (
                  <label
                    key={idx}
                    className="flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-colors"
                    style={{
                      backgroundColor: isChecked ? 'var(--color-surface)' : 'transparent',
                      border: `1px solid ${isChecked ? 'var(--color-primary)' : 'var(--color-border)'}`,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => toggle(idx)}
                      className="mt-1"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm" style={{ color: 'var(--color-text-primary)' }}>{topic.name}</div>
                      {topic.description && (
                        <p className="text-xs mt-1" style={{ color: 'var(--color-text-secondary)' }}>{topic.description}</p>
                      )}
                      {topic.keywords && topic.keywords.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {topic.keywords.map((kw, kIdx) => (
                            <span
                              key={kIdx}
                              className="text-[10px] px-2 py-0.5 rounded-full"
                              style={{ backgroundColor: 'var(--color-border)', color: 'var(--color-text-secondary)' }}
                            >
                              {kw}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </div>
        </div>

        <div className="flex gap-3 justify-end pt-4">
          <AnimatedIconButton
            icon={X}
            label={t.common.cancel}
            onClick={onClose}
            variant="secondary"
            disabled={isAdding}
          />
          <AnimatedIconButton
            icon={isAdding ? Loader2 : Plus}
            label={
              isAdding
                ? (t.common.adding ?? 'Ajout…')
                : `${t.common.addSelection ?? 'Ajouter sélection'} (${selected.size})`
            }
            onClick={handleAdd}
            variant="success"
            disabled={isLoading || isAdding || noneSelected || !!error}
            iconClassName={isAdding ? 'animate-spin' : ''}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
